import { useAppDispatch } from 'store/hooks';
import { awardEnrichmentApi } from 'components/loyalty/AwardEnrichmentsApi';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import {
    MESSAGE_MARKETING_AWARD_DELETE_ERROR,
    MESSAGE_MARKETING_AWARD_DELETE_SUCCESS
} from 'config/messages';
import { removeArrayItem, updateArrayItem } from 'lib/helpers';
import logger from 'lib/logger';
import React from 'react';
import { resetCache } from 'store/match-codes/matchCodesSlice';
import { useSnackbar } from 'notistack';

export const useExternalPerksMutation = ({
    awardEnrichments,
    indexToDelete,
    selectedToDuplicate,
    setAwardEnrichments,
    setIndexToDelete,
    setIsCreateOpen,
    handleResetEditSelection,
    handleResetDuplicateSelection
}: {
    awardEnrichments: IAwardEnrichment[];
    indexToDelete: number;
    selectedToDuplicate?: IAwardEnrichment | null;
    setIsCreateOpen: () => void;
    setAwardEnrichments: React.Dispatch<React.SetStateAction<IAwardEnrichment[]>>;
    setIndexToDelete: React.Dispatch<React.SetStateAction<number>>;
    handleResetEditSelection: () => void;
    handleResetDuplicateSelection: () => void;
}) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [isProcessing, setIsProcessing] = React.useState(false);

    const handleConfirmDelete = React.useCallback(async () => {
        const award = awardEnrichments[indexToDelete];

        if (award) {
            try {
                setIsProcessing(true);
                const res = await awardEnrichmentApi.deleteAward(award._id, { skipResponseBody: true });

                if (!res.ok) {
                    throw new Error(res.body.message);
                }

                setAwardEnrichments(values => removeArrayItem(values, indexToDelete));
                setIndexToDelete(-1);

                enqueueSnackbar(MESSAGE_MARKETING_AWARD_DELETE_SUCCESS, { variant: 'success' });
                dispatch(resetCache());
            } catch (error) {
                logger.error(error.message);
                enqueueSnackbar(MESSAGE_MARKETING_AWARD_DELETE_ERROR, { variant: 'error' });
            }
            setIsProcessing(false);
        }
    }, [awardEnrichments, dispatch, enqueueSnackbar, indexToDelete, setAwardEnrichments, setIndexToDelete]);

    const handleCreateSubmit = React.useCallback(
        (award: IAwardEnrichment) => {
            setAwardEnrichments(awards => [
                ...awards,
                {
                    ...award,
                    id: award._id
                }
            ]);
            dispatch(resetCache());
            if (selectedToDuplicate) {
                handleResetDuplicateSelection();
            } else {
                setIsCreateOpen();
            }
        },
        [selectedToDuplicate, setAwardEnrichments, setIsCreateOpen, handleResetDuplicateSelection, dispatch]
    );
    const handleUpdateSuccess = React.useCallback(
        (award: IAwardEnrichment) => {
            const indexToUpdate = awardEnrichments.findIndex(({ _id }) => _id === award._id);
            if (indexToUpdate !== -1) {
                setAwardEnrichments(awards => updateArrayItem(awards, indexToUpdate, award));
                handleResetEditSelection();
            }
            dispatch(resetCache());
        },
        [awardEnrichments, handleResetEditSelection, setAwardEnrichments, dispatch]
    );

    return {
        isProcessing,
        handleCreateSubmit,
        handleUpdateSuccess,
        handleConfirmDelete
    };
};
